/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import {
	Drawer,
	Box,
	ScrollArea,
	Skeleton,
} from '@mantine/core';

// Mantine form
import {
	useForm,
	isNotEmpty,
	matches,
} from '@mantine/form';

// Styles
import classes from './UpdateAdditionalityProject.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import {
	REGEX_LINKEDIN,
	REGEX_VALID_NAME,
	REGEX_VALID_PARAGRAPHE_RICH_TEXT,
	REGEX_VALID_PLACE,
	REGEX_VALID_RICH_TEXT,
	REGEX_WEBSITE,
	REGEX_X,
} from '@config/constants/regex';

import { UpdateAdditionalityProjectForm } from './UpdateAdditionalityProjectForm';
import {
	LINKEDIN_URL,
	X_URL,
} from '@config/constants/common';

import { formatTextWithHTMLParagraph } from '@utils/formats';
import isNil from 'lodash/isNil';
import { useAdditionalityById } from '../../api/get-additionality-by-id';
import { useAdditionalityProjectUpdate } from '../../hooks/useAdditionalityProjectUpdate';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
type TUpdateAdditionalityProjectProps = {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
	projectId: string;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const UpdateAdditionalityProject = (
	props: TUpdateAdditionalityProjectProps
) => {
	const { projectId } = props;

	/* ******************************** HOOKS *********************************** */

	const { t } = useTranslation();

	// Validators
	const form: any = useForm({
		initialValues: {
			entity: '',
			supportedBy: null,
			name: '',
			countryCode: '',
			place: '',
			cover: null,
			logo: null,
			unit: null,
			technology: '',
			technicalDocuments: null,
			twitter: '',
			linkedin: '',
			website: '',
			description: '',
			active: true,
			dueDate: null,
			openingDate: null,
			financing: 0,
			typeOfFinancing: 'UNLIMITED',
		},

		validate: {
			entity: isNotEmpty(t('global.entityRequired')),
			name: (value) => {
				if (value.length == 0) {
					return t('global.nameRequired');
				}
				if (value.length == 1) {
					return t('form.error.invalidNameLength');
				}
				if (
					!REGEX_VALID_NAME.test(value) ||
					value.length > 40
				) {
					return t('form.error.invalidName');
				}
				return null;
			},
			countryCode: isNotEmpty(
				t('global.countryRequired')
			),

			place: (value) => {
				if (value.length == 0) {
					return t('global.placeRequired');
				}
				if (value.length == 1) {
					return t('form.error.invalidPlaceLength');
				}
				if (
					!REGEX_VALID_PLACE.test(value) ||
					value.length > 50
				) {
					return t('form.error.invalidPlace');
				}
				return null;
			},
			unit: isNotEmpty(t('global.unitRequired')),
			technology: isNotEmpty(
				t('global.technologyRequired')
			),
			cover: isNotEmpty(t('global.coverRequired')),
			description: (value) => {
				if (!REGEX_VALID_RICH_TEXT.test(value)) {
					return t('global.descriptionRequired');
				}
				if (
					!REGEX_VALID_PARAGRAPHE_RICH_TEXT.test(value)
				) {
					return t('form.error.invalidText');
				}
				return null;
			},
			twitter: matches(REGEX_X, 'form.error.invalidX'),
			website: matches(
				REGEX_WEBSITE,
				'form.error.invalidWebsite'
			),
			linkedin: matches(
				REGEX_LINKEDIN,
				'form.error.invalidLinkedin'
			),
			dueDate: (value, values) => {
				return values.typeOfFinancing !== 'UNLIMITED' &&
					value == null
					? t('global.dueDateRequired')
					: null;
			},
			openingDate: (value, values) => {
				return values.typeOfFinancing !== 'UNLIMITED' &&
					value == null
					? t('global.dateOfCommissioningRequired')
					: null;
			},
		},
	});

	const handlecloseDrawer = () => {
		props.onClose();
		form.reset();
	};
	const {
		viewport,
		isOpenModalCancel,
		setIsOpenModalCancel,
		handleCloseDrawerAddAdditionalityProject,
		setInitialAdditionalityData,
	} = useAdditionalityProjectUpdate({
		form: form,
		onBack: () => handlecloseDrawer,
	});

	const { data, isFetching } = useAdditionalityById(
		projectId ? projectId : '',
		props.opened
	);

	useEffect(() => {
		if (!!projectId && !isNil(data)) {
			if (!isNil(data)) {
				setInitialAdditionalityData(data);
			}

			form.setValues({
				name: data.name,
				entity: data.entity ? data.entity : '',
				countryCode: data.location
					? data.location.countryCode
					: '',
				place:
					data.location && data.location.place
						? data.location.place
						: '',
				unit: data.unit,
				cover: data.cover,
				logo: data.logo,
				dueDate: data.dueDate
					? new Date(data.dueDate)
					: null,
				openingDate: data.openingDate
					? new Date(data.openingDate)
					: null,

				typeOfFinancing:
					data.financing && data.financing.unlimited
						? 'UNLIMITED'
						: 'LIMITED',
				financing:
					data.financing && data.financing.target_euro
						? data.financing.target_euro
						: 0,
				technology: data.technology,
				description: data.description
					? formatTextWithHTMLParagraph(data.description)
					: '',

				twitter: data.social?.twitter
					? X_URL + '/' + data.social.twitter
					: '',
				linkedin: data.social?.linkedIn
					? LINKEDIN_URL + '/' + data.social.linkedIn
					: '',
				active: data.active,
				supportedBy: data?.supportedBy || null,
				website: data.social?.website
					? data.social?.website
					: '',
			});
			form.resetDirty();
		}
	}, [isFetching]);

	/* ******************************** RENDERING ******************************* */

	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={async () => {
						await handleCloseDrawerAddAdditionalityProject(
							form,
							data
						);
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('global.backToProjectDetailsSure')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('global.backToProjectDetails')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						{!isFetching && (
							<ScrollArea
								viewportRef={viewport}
								className={classes.containerContent}
							>
								<UpdateAdditionalityProjectForm
									projectId={projectId}
									dataApi={data}
									isPreviousAdditionalityProjectDetails={
										true
									}
									form={form}
									onCancel={() => {
										setIsOpenModalCancel(true);
									}}
									onBack={handlecloseDrawer}
								/>
							</ScrollArea>
						)}
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
